import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  CheckCircle,
  Info,
  AlertTriangle,
  HelpCircle,
} from 'lucide-react';
import { DetailShell, SerifHeading } from '../h';
import docs from '../../data/docs.json';

/* Category colour pairs for the tinted pill (matches 6.1 tile colours). */
const CATEGORY_META = {
  'Getting Started': {
    label: 'Getting started',
    pillBg: '#dcfce7',
    pillBorder: '#bbf7d0',
    pillColor: '#047857',
    Icon: CheckCircle,
  },
  'Understanding Higlo': {
    label: 'Understanding Higlo',
    pillBg: '#dbeafe',
    pillBorder: '#bfdbfe',
    pillColor: '#1e40af',
    Icon: Info,
  },
  Troubleshooting: {
    label: 'Troubleshooting',
    pillBg: '#fef3c7',
    pillBorder: '#fde68a',
    pillColor: '#b45309',
    Icon: AlertTriangle,
  },
  FAQ: {
    label: 'FAQ',
    pillBg: '#f3e8ff',
    pillBorder: '#e9d5ff',
    pillColor: '#6d28d9',
    Icon: HelpCircle,
  },
};

function minutesToRead(content) {
  if (!content) return 1;
  const words = content.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

/* Strip the first H1 from the article body — we render the title separately
   using SerifHeading so the markdown shouldn't emit it again. */
function stripLeadingHeading(content) {
  if (!content) return '';
  return content.replace(/^\s*#\s+[^\n]+\n?/, '');
}

/* Resolve a relative .md markdown link against the current article's slug,
   producing a target slug that matches docs.json (no .md, no leading slash).
   Returns null for non-internal links (external, mailto, anchor, absolute). */
function resolveInternalSlug(href, currentSlug) {
  if (!href) return null;
  if (/^([a-z][a-z0-9+.-]*:|#|\/)/i.test(href)) return null;
  if (!/\.md(?:[?#]|$)/i.test(href)) return null;
  const cleaned = href.replace(/[?#].*$/, '').replace(/\.md$/i, '');
  const baseParts = (currentSlug || '').split('/').slice(0, -1);
  for (const part of cleaned.split('/')) {
    if (part === '..') baseParts.pop();
    else if (part && part !== '.') baseParts.push(part);
  }
  return baseParts.join('/');
}

/**
 * ArticleView — v9 spec 6.3.
 * DetailShell with a "Help · {Category}" breadcrumb, a tinted category pill,
 * and the article body rendered via react-markdown.
 *
 * The slug is pulled from react-router's useParams(). Because the route uses
 * a splat ("/article/:slug/*"), we re-stitch the category + filename into a
 * single "category/filename" slug.
 */
export function ArticleView() {
  const navigate = useNavigate();
  const params = useParams();

  /* react-router will give us { slug: "getting-started", "*": "understanding-the-basics" }
     for a route like `/app/help/article/:slug/*`. Rejoin them so it matches the
     docs.json slug shape ("category/filename"). */
  const joinedSlug = React.useMemo(() => {
    const head = params.slug || '';
    const rest = params['*'] || '';
    const combined = head && rest ? `${head}/${rest}` : head || rest;
    // Tolerate stray .md from hard-navigated relative links.
    return combined.replace(/\.md$/i, '');
  }, [params]);

  const articles = Array.isArray(docs.articles) ? docs.articles : [];
  const article = articles.find((a) => a.slug === joinedSlug);

  const meta = article ? CATEGORY_META[article.category] : null;
  const readTime = article ? minutesToRead(article.content) : 0;

  const breadcrumb = article
    ? meta?.label || article.category
    : 'Help';

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/app/help');
  };

  return (
    <DetailShell onBack={handleBack} breadcrumb={breadcrumb}>
      <div className="px-4 pb-6">
        {!article ? (
          <div className="pt-6">
            <SerifHeading size="lg" className="mb-2">
              Article not found
            </SerifHeading>
            <p className="text-[13px] text-slate-500">
              We couldn't find an article for "{joinedSlug}".
            </p>
          </div>
        ) : (
          <>
            {meta && (
              <div
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-4"
                style={{
                  background: meta.pillBg,
                  border: `1px solid ${meta.pillBorder}`,
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: meta.pillColor }}
                />
                <span
                  className="text-[11px] font-semibold"
                  style={{ color: meta.pillColor }}
                >
                  {meta.label} · {readTime} min read
                </span>
              </div>
            )}

            <SerifHeading size="lg" className="mb-4">
              {article.title}
            </SerifHeading>

            <div className="article-body text-[13px] text-slate-700 leading-relaxed">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ node, ...p }) => (
                    <h1
                      className="font-heading font-medium text-slate-900 text-[22px] leading-[1.2] mb-3 mt-4"
                      {...p}
                    />
                  ),
                  h2: ({ node, ...p }) => (
                    <h2
                      className="text-[17px] font-semibold text-slate-900 mb-3 mt-5"
                      {...p}
                    />
                  ),
                  h3: ({ node, ...p }) => (
                    <h3
                      className="text-[15px] font-semibold text-slate-900 mb-2 mt-4"
                      {...p}
                    />
                  ),
                  p: ({ node, ...p }) => (
                    <p
                      className="text-[13px] text-slate-700 leading-relaxed mb-3"
                      {...p}
                    />
                  ),
                  ul: ({ node, ...p }) => (
                    <ul
                      className="text-[13px] text-slate-700 space-y-1.5 mb-4 leading-relaxed list-disc ml-5"
                      {...p}
                    />
                  ),
                  ol: ({ node, ...p }) => (
                    <ol
                      className="text-[13px] text-slate-700 space-y-2 leading-relaxed list-decimal ml-5 mb-6"
                      {...p}
                    />
                  ),
                  blockquote: ({ node, ...p }) => (
                    <div
                      className="rounded-2xl p-3 mb-4 flex items-start gap-2 bg-blue-50 border border-blue-200"
                    >
                      <Info
                        className="w-4 h-4 flex-shrink-0 mt-0.5"
                        style={{ color: '#1e40af' }}
                        strokeWidth={2}
                      />
                      <div
                        className="text-[12px] text-slate-700 leading-relaxed [&>p]:!mb-0 [&>p]:!text-[12px] [&>p]:!text-slate-700"
                        {...p}
                      />
                    </div>
                  ),
                  a: ({ node, href, children, ...p }) => {
                    const targetSlug = resolveInternalSlug(href, article?.slug);
                    const isInternal =
                      targetSlug && articles.some((a) => a.slug === targetSlug);
                    if (isInternal) {
                      const to = `/app/help/article/${targetSlug}`;
                      return (
                        <a
                          href={to}
                          onClick={(e) => {
                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                            e.preventDefault();
                            navigate(to);
                          }}
                          className="text-slate-900 underline underline-offset-2 hover:text-slate-700"
                        >
                          {children}
                        </a>
                      );
                    }
                    const isExternal = /^https?:\/\//i.test(href || '');
                    return (
                      <a
                        href={href}
                        className="text-slate-900 underline underline-offset-2 hover:text-slate-700"
                        {...(isExternal
                          ? { target: '_blank', rel: 'noopener noreferrer' }
                          : {})}
                        {...p}
                      >
                        {children}
                      </a>
                    );
                  },
                  img: ({ node, src, alt }) => (
                    <figure className="my-4 flex flex-col items-center">
                      <img
                        src={src}
                        alt={alt || ''}
                        loading="lazy"
                        className="max-w-full max-h-[600px] w-auto object-contain rounded-2xl border border-slate-200 shadow-sm bg-white"
                      />
                      {alt ? (
                        <figcaption className="text-[11px] text-slate-400 text-center mt-1.5 max-w-md">
                          {alt}
                        </figcaption>
                      ) : null}
                    </figure>
                  ),
                  code: ({ node, inline, ...p }) =>
                    inline ? (
                      <code
                        className="px-1 py-0.5 rounded bg-slate-100 text-[12px] font-mono"
                        {...p}
                      />
                    ) : (
                      <code
                        className="block px-3 py-2 rounded-lg bg-slate-100 text-[12px] font-mono overflow-x-auto"
                        {...p}
                      />
                    ),
                }}
              >
                {stripLeadingHeading(article.content || '')}
              </ReactMarkdown>
            </div>
          </>
        )}
      </div>
    </DetailShell>
  );
}

export default ArticleView;
